import { useEffect, useMemo, useState } from 'react'
import { FileWarning, Zap } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { AsaasIntegrationService, type UngeneratedInvoice } from '@/services/modules/AsaasIntegrationService'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'

function currentMonthRange() {
  const now = new Date()
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)) }
}
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const ymd = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—')

const EXPORT_COLUMNS: ExportColumn<UngeneratedInvoice>[] = [
  { header: 'Tipo',       accessor: (r) => (r.entityType === 'company' ? 'Empresa' : 'Associado') },
  { header: 'Cliente',    accessor: (r) => r.entityName },
  { header: 'Código',     accessor: (r) => r.code ?? '' },
  { header: 'Vencimento', accessor: (r) => (r.dueDate ? ymd(r.dueDate) : '') },
  { header: 'Valor',      accessor: (r) => brl(r.amount) },
]

export function AsaasUngeneratedInvoicesPage() {
  const init = useMemo(currentMonthRange, [])
  const [from, setFrom] = useState(init.from)
  const [to, setTo] = useState(init.to)
  const [applied, setApplied] = useState(init)
  const [rows, setRows] = useState<UngeneratedInvoice[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const { can } = useAuthority()
  const canGenerate = can('integrations.asaas.ungenerated_generate')

  useEffect(() => {
    let alive = true
    setLoading(true)
    AsaasIntegrationService.ungeneratedInvoices(applied.from, applied.to)
      .then((res) => { if (alive) setRows(res.data) })
      .catch(() => { if (alive) toast.error('Erro ao carregar as faturas.') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [applied])

  async function generate(r: UngeneratedInvoice) {
    setBusyId(r.id)
    try {
      const res = await AsaasIntegrationService.generateInvoice(r.id)
      if (res.externalId) {
        toast.success({ title: 'Cobrança gerada', description: `Boleto criado no Asaas para ${r.entityName}.` })
        setRows((prev) => prev.filter((x) => x.id !== r.id))
      } else {
        toast.warning({ title: 'Sem retorno', description: 'O Asaas não retornou o id da cobrança.' })
      }
    } catch (e) {
      toast.error({ title: 'Não foi possível gerar', description: extractApiError(e).message })
    } finally { setBusyId(null) }
  }

  const columns: DataTableColumn<UngeneratedInvoice>[] = [
    { key: 'entityName', header: 'Cliente', alwaysVisible: true, render: (r) => (
      <div className="flex items-center gap-2">
        <Badge variant={r.entityType === 'company' ? 'blue' : 'gray'}>{r.entityType === 'company' ? 'Empresa' : 'Associado'}</Badge>
        <span className="font-medium text-gray-800">{r.entityName}</span>
        <span className="text-[11px] text-gray-400">#{r.code}</span>
      </div>
    ) },
    { key: 'dueDate', header: 'Vencimento', align: 'center', className: 'w-32', render: (r) => ymd(r.dueDate) },
    { key: 'amount', header: 'Valor', align: 'center', className: 'w-32', render: (r) => brl(r.amount) },
    ...(canGenerate ? [{ key: 'actions', header: '', align: 'center', className: 'w-40', render: (r: UngeneratedInvoice) => (
      <Button size="sm" iconLeft={<Zap size={13} />} loading={busyId === r.id} onClick={() => generate(r)}>Gerar no Asaas</Button>
    ) } as DataTableColumn<UngeneratedInvoice>] : []),
  ]

  function exportAll() {
    if (!rows.length) { toast.error('Nenhum registro para exportar.'); return }
    exportToXlsx('faturas-nao-geradas', rows, EXPORT_COLUMNS)
  }

  function apply() {
    if (from && to && from > to) { toast.error({ title: 'Período inválido', description: 'A data inicial não pode ser maior que a final.' }); return }
    setApplied({ from, to })
  }

  return (
    <>
      <PageHeader
        title="Faturas não geradas"
        subtitle="Faturas pendentes no sistema sem cobrança vinculada no Asaas"
        crumbs={[{ label: 'Integrações' }, { label: 'Asaas' }, { label: 'Faturas não geradas' }]}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md bg-white p-4 ring-1 ring-gray-200">
        <Input label="De" type="date" value={from} onChange={(e) => setFrom(e.target.value)} wrapperClassName="w-44" />
        <Input label="Até" type="date" value={to} onChange={(e) => setTo(e.target.value)} wrapperClassName="w-44" />
        <Button onClick={apply}>Aplicar</Button>
        <div className="ml-auto flex items-center gap-2 text-sm text-gray-500">
          <FileWarning size={16} className="text-amber-500" /> {rows.length} fatura(s) sem cobrança
        </div>
      </div>

      <DataTable
        tableId="asaas.ungenerated"
        exportable
        onExportAll={exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={(r) => r.id}
        emptyMessage="Nenhuma fatura pendente sem cobrança no período."
      />
    </>
  )
}

export default AsaasUngeneratedInvoicesPage
